import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { ScrollText } from "lucide-react";

import { REGISTER } from "../../constants";
import { useTranslation } from '../../language';
import TextInput from "../../components/Forms/TextInput";
import PasswordInput from "../../components/Forms/PasswordInput";
import CheckboxInput from "../../components/Forms/CheckboxInput";
import ButtonForm from "../../components/Button/ButtonForm";
import ButtonsTextGoTo from "../../components/Button/ButtonTextGo";
import FormAlert from "../../components/Alert/FormAlert";
import CustomDialog from "../../components/Dialog/DiaCompMedium"
import SelectInputNew from "../../components/Forms/SelectInputNew";

import TermsOfServiceEN from "./TermsOfServices_en";
import TermsOfServiceDE from "./TermsOfServices_de";

// ----------------- Validation -----------------
const usernameRegExp = /^[\w.@+-]+$/;
const passwordRegExp =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()[\]{};:'",.<>?\\|`~\-_=+]).{8,}$/;
const phoneRegExp = /^\+?[1-9]\d{1,14}$/;

const createValidationSchema = (t) => Yup.object().shape({
  username: Yup.string()
    .matches(usernameRegExp, t("val_username_chars"))
    .min(3, t("val_username_min"))
    .max(150, t("val_username_max"))
    .required(t("val_username_req")),
  email: Yup.string()
    .email(t("val_email_valid"))
    .max(254, t("val_email_max"))
    .required(t("val_email_req")),
  password: Yup.string()
    .matches(passwordRegExp, t("val_pw_strong"))
    .required(t("val_pw_req")),
  password2: Yup.string()
    .oneOf([Yup.ref("password"), null], t("val_pw2_match"))
    .required(t("val_pw2_req")),
  first_name: Yup.string()
    .max(150, t("val_first_max"))
    .required(t("val_first_req")),
  last_name: Yup.string()
    .max(150, t("val_last_max"))
    .required(t("val_last_req")),
  phone_number: Yup.string()
    .matches(phoneRegExp, t("val_phone"))
    .nullable(),
  terms_confirmed: Yup.bool()
    .oneOf([true], t("val_terms_req"))
    .required(t("val_terms_req")),
});

// ----------------- Component -----------------
const RegisterForm = () => {

  const { t, language, changeLanguage } = useTranslation();

  const validationSchema = useMemo(() => createValidationSchema(t), [language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { changeLanguage('de'); }, []);

  const theme = useTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/EmailPage";

  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [errorMessage, setErrorMessage] = useState(null);
  // Separate state for each password field
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [open, setOpen] = useState(false);

  const handleTogglePassword = () => setShowPassword((p) => !p);
  const handleTogglePassword2 = () => setShowPassword2((p) => !p);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  const handleSubmit = async (values, { setFieldError, setSubmitting }) => {
    try {
      setErrorMessage(null);
      const response = await axios.post(REGISTER, values);
       if (values.language) {
        changeLanguage(values.language);
      }

      if (response) {
        navigate(from, { replace: true });
      }
    } catch (error) {
      if (error.response?.data) {
        const apiErrors = error.response.data;
        Object.keys(apiErrors).forEach((field) => {
          const val = apiErrors[field];
          const message = Array.isArray(val)
            ? val.join(" ")
            : typeof val === "object" && val?.message
            ? val.message
            : String(val);
          setFieldError(field, message);
        });
        setErrorMessage({ message: t("val_form_fix") });
      } else {
        setErrorMessage({ message: t("val_server_error") });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {errorMessage && (
        <FormAlert
          severity="error"
          title={t("val_form_error")}
          message={errorMessage.message}
          onClose={() => setErrorMessage(null)}
          sx={{ mt: 2 }}
        />
      )}

      <Formik
        initialValues={{
          language: 'de',
          username: "",
          password: "",
          password2: "",
          email: location.state?.prefillEmail || "",
          first_name: "",
          last_name: "",
          phone_number: "",
          terms_confirmed: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>

            <SelectInputNew
                label={t("reg_lang")}
                name="language"
                onChange={(e) => {
                  setFieldValue('language', e.target.value);
                  changeLanguage(e.target.value);
                  setSelectedLanguage(e.target.value);
                }}
                options={[
                  { value: "", label: t("reg_lang") },
                  { value: "en", label: t("reg_en") },
                  { value: "de", label: t("reg_de") },
                ]}
              />

            <TextInput
              label={t("reg_first")}
              name="first_name"
              placeholder={t("reg_first")}
            />

            <TextInput
              label={t("reg_last")}
              name="last_name"
              placeholder={t("reg_last")}
            />

            <TextInput
              label={t("reg_username")}
              name="username"
              placeholder={t("reg_username")}
            />

            <PasswordInput
              label={t("reg_pw")}
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={t("reg_pw")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordInput
              label={t("reg_pwr")}
              name="password2"
              type={showPassword ? "text" : "password"}
              placeholder={t("reg_pwr")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword2} edge="end">
                      {showPassword2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextInput
              label="Email"
              name="email"
              type="email"
              placeholder="Email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextInput
              label={t("reg_mobile")}
              name="phone_number"
              type="tel"
              placeholder="+436641234567"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mt: 1 }}>
              <CheckboxInput name="terms_confirmed" color="warning.main">
                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("reg_tc")}</Typography>
              </CheckboxInput>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 400 }}>{t("reg_tcr")}</Typography>
                <IconButton onClick={handleOpen} size="small" sx={{ ml: 0.5, "&:hover": { transform: "scale(1.2)" } }}>
                  <ScrollText size={14} />
                </IconButton>
              </Box>
            </Box>

            <ButtonForm name={t("reg_submit")} disabled={isSubmitting} />

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ mt: { xs: 3 }, fontWeight: 400 }}
              >
                {t("reg_reg")}
              </Typography>
              <ButtonsTextGoTo to="/Login" name={t("reg_signin")} />
            </Box>
          </Form>
        )}
      </Formik>

      <CustomDialog
        open={open}
        onClose={handleClose}
        title=""
        content={selectedLanguage === 'de' ? <TermsOfServiceDE /> : <TermsOfServiceEN />}
      />
    </>
  );
};

export default RegisterForm;
